// TERRA – Real retrocast data extracted from pipeline output
// Niscemi 2026 landslide + Emilia-Romagna 2023 flood

export type EWLevel = 'GREEN' | 'YELLOW' | 'ORANGE' | 'RED'

export interface EWTimelinePoint {
  date: string
  ew: number
  level: EWLevel
  rainfall_mm: number
  swi: number
  insar_accel: number
}

export interface InSARPoint {
  date: string
  displacement_mm: number
  velocity_mm_yr: number
  acceleration_mm_yr2: number
}

export interface RainfallDailyPoint {
  date: string
  rainfall_mm: number
  cumulative_mm: number
}

export interface RetrocastScenario {
  id: string
  name: string
  subtitle: string
  region: string
  eventDate: string
  eventType: 'landslide' | 'flood'
  analysisStart: string
  analysisEnd: string
  lat: number
  lon: number
  maxEW: number
  maxEWDate: string
  firstOrangeDate: string
  firstRedDate: string | null
  leadTimeDays: number
  totalRainfallMm: number
  maxDailyRainfallMm: number
  peakDisplacementMm: number
  peakVelocityMmYr: number
  ewTimeline: EWTimelinePoint[]
  insarData: InSARPoint[]
  rainfallData: RainfallDailyPoint[]
  keyEvents: { date: string; label: string; type: 'info' | 'warning' | 'critical' }[]
}

// ================================================================
// NISCEMI – Landslide 15 Jan 2026
// Max EW: 9.57 RED | Lead time: 32 days from first ORANGE
// ================================================================

const NISCEMI_EW: EWTimelinePoint[] = [
  { date: '2025-10-01', ew: 2.83, level: 'GREEN', rainfall_mm: 0, swi: 49.5, insar_accel: 0 },
  { date: '2025-10-04', ew: 5.62, level: 'YELLOW', rainfall_mm: 25, swi: 51.1, insar_accel: 0.827 },
  { date: '2025-10-06', ew: 5.65, level: 'YELLOW', rainfall_mm: 0, swi: 40.7, insar_accel: 1.0 },
  { date: '2025-10-11', ew: 5.83, level: 'YELLOW', rainfall_mm: 13.7, swi: 46.1, insar_accel: 1.0 },
  { date: '2025-10-16', ew: 5.93, level: 'YELLOW', rainfall_mm: 27.9, swi: 41.7, insar_accel: 1.0 },
  { date: '2025-10-21', ew: 5.83, level: 'YELLOW', rainfall_mm: 17.3, swi: 45.1, insar_accel: 1.0 },
  { date: '2025-10-26', ew: 6.21, level: 'YELLOW', rainfall_mm: 56.6, swi: 39.3, insar_accel: 1.0 },
  { date: '2025-10-31', ew: 5.76, level: 'YELLOW', rainfall_mm: 0, swi: 47.7, insar_accel: 1.0 },
  { date: '2025-11-05', ew: 5.98, level: 'YELLOW', rainfall_mm: 7.4, swi: 51.4, insar_accel: 1.0 },
  { date: '2025-11-10', ew: 6.68, level: 'YELLOW', rainfall_mm: 51.7, swi: 57.9, insar_accel: 1.0 },
  { date: '2025-11-15', ew: 6.03, level: 'YELLOW', rainfall_mm: 7.4, swi: 52.6, insar_accel: 1.0 },
  { date: '2025-11-20', ew: 6.18, level: 'YELLOW', rainfall_mm: 2.5, swi: 57.6, insar_accel: 1.0 },
  { date: '2025-11-25', ew: 6.28, level: 'YELLOW', rainfall_mm: 15.6, swi: 57.0, insar_accel: 1.0 },
  { date: '2025-11-30', ew: 6.44, level: 'YELLOW', rainfall_mm: 13.5, swi: 61.4, insar_accel: 1.0 },
  { date: '2025-12-05', ew: 6.52, level: 'YELLOW', rainfall_mm: 13.2, swi: 63.6, insar_accel: 1.0 },
  { date: '2025-12-10', ew: 6.81, level: 'YELLOW', rainfall_mm: 9.2, swi: 71.8, insar_accel: 1.0 },
  { date: '2025-12-14', ew: 7.03, level: 'ORANGE', rainfall_mm: 29.9, swi: 72.1, insar_accel: 1.0 },
  { date: '2025-12-18', ew: 7.19, level: 'ORANGE', rainfall_mm: 19.4, swi: 78.8, insar_accel: 1.0 },
  { date: '2025-12-21', ew: 7.14, level: 'ORANGE', rainfall_mm: 22.2, swi: 76.7, insar_accel: 1.0 },
  { date: '2025-12-26', ew: 7.32, level: 'ORANGE', rainfall_mm: 15.0, swi: 83.2, insar_accel: 1.0 },
  { date: '2025-12-30', ew: 7.32, level: 'ORANGE', rainfall_mm: 21.3, swi: 81.5, insar_accel: 1.0 },
  { date: '2026-01-04', ew: 7.96, level: 'ORANGE', rainfall_mm: 79.3, swi: 83.1, insar_accel: 1.0 },
  { date: '2026-01-07', ew: 7.45, level: 'ORANGE', rainfall_mm: 19.6, swi: 85.2, insar_accel: 1.0 },
  { date: '2026-01-09', ew: 8.24, level: 'ORANGE', rainfall_mm: 85.7, swi: 88.5, insar_accel: 1.0 },
  { date: '2026-01-14', ew: 9.30, level: 'RED', rainfall_mm: 185.3, swi: 90.1, insar_accel: 1.0 },
  { date: '2026-01-15', ew: 9.45, level: 'RED', rainfall_mm: 316.3, swi: 90.1, insar_accel: 1.0 },
  { date: '2026-01-18', ew: 8.06, level: 'ORANGE', rainfall_mm: 50.6, swi: 92.7, insar_accel: 1.0 },
  { date: '2026-01-22', ew: 6.40, level: 'YELLOW', rainfall_mm: 0, swi: 63.9, insar_accel: 1.0 },
  { date: '2026-01-29', ew: 6.65, level: 'YELLOW', rainfall_mm: 14.0, swi: 66.6, insar_accel: 1.0 },
  { date: '2026-02-08', ew: 6.73, level: 'YELLOW', rainfall_mm: 34.9, swi: 63.3, insar_accel: 1.0 },
  { date: '2026-02-18', ew: 6.56, level: 'YELLOW', rainfall_mm: 11.1, swi: 65.0, insar_accel: 1.0 },
  { date: '2026-02-28', ew: 6.28, level: 'YELLOW', rainfall_mm: 0, swi: 60.8, insar_accel: 1.0 },
]

const NISCEMI_INSAR: InSARPoint[] = [
  { date: '2023-10-01', displacement_mm: -3.31, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2023-12-01', displacement_mm: -13.17, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-02-01', displacement_mm: -14.21, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-04-01', displacement_mm: -8.10, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-06-01', displacement_mm: -6.25, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-08-01', displacement_mm: -11.61, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-10-01', displacement_mm: -18.37, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2024-12-01', displacement_mm: -24.99, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-02-01', displacement_mm: -29.15, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-04-01', displacement_mm: -23.87, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-06-01', displacement_mm: -18.20, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-08-01', displacement_mm: -22.56, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-10-01', displacement_mm: -30.55, velocity_mm_yr: -15.0, acceleration_mm_yr2: 0 },
  { date: '2025-11-01', displacement_mm: -34.19, velocity_mm_yr: -29.24, acceleration_mm_yr2: -170.88 },
  { date: '2025-12-01', displacement_mm: -41.04, velocity_mm_yr: -76.86, acceleration_mm_yr2: -571.44 },
  { date: '2026-01-01', displacement_mm: -56.62, velocity_mm_yr: -173.88, acceleration_mm_yr2: -1164.24 },
  { date: '2026-02-01', displacement_mm: -55.27, velocity_mm_yr: -15.0, acceleration_mm_yr2: 1906.56 },
]

const NISCEMI_RAINFALL: RainfallDailyPoint[] = [
  { date: '2025-10-03', rainfall_mm: 25.0, cumulative_mm: 25.0 },
  { date: '2025-10-08', rainfall_mm: 25.6, cumulative_mm: 50.6 },
  { date: '2025-10-10', rainfall_mm: 13.7, cumulative_mm: 64.3 },
  { date: '2025-10-14', rainfall_mm: 23.3, cumulative_mm: 91.6 },
  { date: '2025-10-19', rainfall_mm: 16.1, cumulative_mm: 112.3 },
  { date: '2025-10-26', rainfall_mm: 50.0, cumulative_mm: 170.1 },
  { date: '2025-10-28', rainfall_mm: 14.6, cumulative_mm: 196.2 },
  { date: '2025-11-06', rainfall_mm: 19.5, cumulative_mm: 227.7 },
  { date: '2025-11-09', rainfall_mm: 40.6, cumulative_mm: 279.4 },
  { date: '2025-11-23', rainfall_mm: 15.6, cumulative_mm: 322.3 },
  { date: '2025-11-29', rainfall_mm: 13.5, cumulative_mm: 336.8 },
  { date: '2025-12-04', rainfall_mm: 13.2, cumulative_mm: 360.7 },
  { date: '2025-12-14', rainfall_mm: 14.4, cumulative_mm: 407.6 },
  { date: '2025-12-16', rainfall_mm: 19.4, cumulative_mm: 434.5 },
  { date: '2025-12-22', rainfall_mm: 18.0, cumulative_mm: 474.7 },
  { date: '2025-12-27', rainfall_mm: 18.6, cumulative_mm: 518.3 },
  { date: '2025-12-29', rainfall_mm: 18.5, cumulative_mm: 536.8 },
  { date: '2026-01-02', rainfall_mm: 50.0, cumulative_mm: 595.5 },
  { date: '2026-01-03', rainfall_mm: 29.3, cumulative_mm: 624.8 },
  { date: '2026-01-07', rainfall_mm: 19.6, cumulative_mm: 644.4 },
  { date: '2026-01-08', rainfall_mm: 20.7, cumulative_mm: 665.1 },
  { date: '2026-01-09', rainfall_mm: 45.4, cumulative_mm: 710.5 },
  { date: '2026-01-10', rainfall_mm: 29.4, cumulative_mm: 739.9 },
  { date: '2026-01-11', rainfall_mm: 38.2, cumulative_mm: 778.1 },
  { date: '2026-01-13', rainfall_mm: 63.2, cumulative_mm: 841.3 },
  { date: '2026-01-14', rainfall_mm: 122.1, cumulative_mm: 963.4 },
  { date: '2026-01-15', rainfall_mm: 131.0, cumulative_mm: 1094.4 },
  { date: '2026-01-16', rainfall_mm: 32.8, cumulative_mm: 1127.2 },
  { date: '2026-02-07', rainfall_mm: 19.3, cumulative_mm: 1227.2 },
  { date: '2026-02-11', rainfall_mm: 35.6, cumulative_mm: 1273.6 },
  { date: '2026-02-19', rainfall_mm: 13.5, cumulative_mm: 1304.7 },
  { date: '2026-02-28', rainfall_mm: 0, cumulative_mm: 1310.2 },
]

// ================================================================
// EMILIA-ROMAGNA – Flood 16 May 2023
// Max EW: 8.43 ORANGE | Lead time: 14 days from first YELLOW
// Drought-to-saturation paradox: SWI from 10th to 90th percentile in 10 days
// ================================================================

const EMILIA_EW: EWTimelinePoint[] = [
  { date: '2022-11-01', ew: 1.72, level: 'GREEN', rainfall_mm: 0, swi: 25.8, insar_accel: 0 },
  { date: '2022-11-15', ew: 1.76, level: 'GREEN', rainfall_mm: 1.8, swi: 26.4, insar_accel: 0 },
  { date: '2022-12-06', ew: 1.72, level: 'GREEN', rainfall_mm: 0, swi: 34.1, insar_accel: 0 },
  { date: '2022-12-27', ew: 2.02, level: 'GREEN', rainfall_mm: 14.8, swi: 29.9, insar_accel: 0 },
  { date: '2023-01-10', ew: 2.12, level: 'GREEN', rainfall_mm: 19.8, swi: 33.5, insar_accel: 0 },
  { date: '2023-01-31', ew: 1.75, level: 'GREEN', rainfall_mm: 1.5, swi: 32.2, insar_accel: 0 },
  { date: '2023-02-14', ew: 1.72, level: 'GREEN', rainfall_mm: 0, swi: 31.3, insar_accel: 0 },
  { date: '2023-02-28', ew: 1.80, level: 'GREEN', rainfall_mm: 3.7, swi: 33.0, insar_accel: 0 },
  { date: '2023-03-14', ew: 1.72, level: 'GREEN', rainfall_mm: 0, swi: 29.2, insar_accel: 0 },
  { date: '2023-03-28', ew: 1.84, level: 'GREEN', rainfall_mm: 6.0, swi: 27.1, insar_accel: 0 },
  { date: '2023-04-11', ew: 1.72, level: 'GREEN', rainfall_mm: 0, swi: 26.2, insar_accel: 0 },
  { date: '2023-04-25', ew: 1.91, level: 'GREEN', rainfall_mm: 9.1, swi: 23.7, insar_accel: 0 },
  { date: '2023-05-02', ew: 5.17, level: 'YELLOW', rainfall_mm: 170.8, swi: 45.5, insar_accel: 0 },
  { date: '2023-05-07', ew: 4.61, level: 'GREEN', rainfall_mm: 119.0, swi: 62.3, insar_accel: 0 },
  { date: '2023-05-09', ew: 4.82, level: 'GREEN', rainfall_mm: 82.6, swi: 75.7, insar_accel: 0 },
  { date: '2023-05-14', ew: 6.58, level: 'YELLOW', rainfall_mm: 132.6, swi: 86.5, insar_accel: 0 },
  { date: '2023-05-15', ew: 7.68, level: 'ORANGE', rainfall_mm: 292.7, swi: 83.0, insar_accel: 0 },
  { date: '2023-05-16', ew: 8.02, level: 'ORANGE', rainfall_mm: 502.5, swi: 87.8, insar_accel: 0 },
  { date: '2023-05-18', ew: 8.43, level: 'ORANGE', rainfall_mm: 502.5, swi: 90.0, insar_accel: 0 },
  { date: '2023-05-21', ew: 5.48, level: 'YELLOW', rainfall_mm: 64.1, swi: 90.4, insar_accel: 0 },
  { date: '2023-05-22', ew: 4.45, level: 'GREEN', rainfall_mm: 4.8, swi: 92.6, insar_accel: 0 },
  { date: '2023-05-30', ew: 4.95, level: 'GREEN', rainfall_mm: 31.0, swi: 92.3, insar_accel: 0 },
  { date: '2023-06-06', ew: 4.15, level: 'GREEN', rainfall_mm: 56.5, swi: 73.6, insar_accel: 0 },
  { date: '2023-06-13', ew: 2.01, level: 'GREEN', rainfall_mm: 5.0, swi: 57.7, insar_accel: 0 },
  { date: '2023-06-27', ew: 3.30, level: 'GREEN', rainfall_mm: 49.7, swi: 53.3, insar_accel: 0 },
]

const EMILIA_INSAR: InSARPoint[] = [
  { date: '2020-11-01', displacement_mm: -2.08, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2021-03-01', displacement_mm: -3.57, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2021-07-01', displacement_mm: -3.61, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2021-11-01', displacement_mm: -8.64, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2022-03-01', displacement_mm: -6.96, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2022-07-01', displacement_mm: -6.69, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2022-11-01', displacement_mm: -14.84, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2023-01-01', displacement_mm: -16.99, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2023-03-01', displacement_mm: -15.80, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2023-05-01', displacement_mm: -15.18, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
  { date: '2023-06-01', displacement_mm: -14.22, velocity_mm_yr: -4.0, acceleration_mm_yr2: 0 },
]

const EMILIA_RAINFALL: RainfallDailyPoint[] = [
  { date: '2022-11-15', rainfall_mm: 1.8, cumulative_mm: 1.8 },
  { date: '2022-11-29', rainfall_mm: 10.2, cumulative_mm: 15.4 },
  { date: '2022-12-13', rainfall_mm: 5.1, cumulative_mm: 25.0 },
  { date: '2022-12-27', rainfall_mm: 8.5, cumulative_mm: 38.0 },
  { date: '2023-01-10', rainfall_mm: 12.0, cumulative_mm: 55.0 },
  { date: '2023-01-24', rainfall_mm: 0, cumulative_mm: 60.0 },
  { date: '2023-02-07', rainfall_mm: 8.0, cumulative_mm: 72.0 },
  { date: '2023-02-21', rainfall_mm: 4.0, cumulative_mm: 80.0 },
  { date: '2023-03-07', rainfall_mm: 3.3, cumulative_mm: 88.0 },
  { date: '2023-03-21', rainfall_mm: 4.3, cumulative_mm: 95.0 },
  { date: '2023-04-04', rainfall_mm: 0.8, cumulative_mm: 100.0 },
  { date: '2023-04-18', rainfall_mm: 5.8, cumulative_mm: 112.0 },
  { date: '2023-04-30', rainfall_mm: 15.0, cumulative_mm: 135.0 },
  { date: '2023-05-01', rainfall_mm: 48.2, cumulative_mm: 183.2 },
  { date: '2023-05-02', rainfall_mm: 72.8, cumulative_mm: 256.0 },
  { date: '2023-05-03', rainfall_mm: 45.0, cumulative_mm: 301.0 },
  { date: '2023-05-10', rainfall_mm: 12.0, cumulative_mm: 340.0 },
  { date: '2023-05-14', rainfall_mm: 55.0, cumulative_mm: 420.0 },
  { date: '2023-05-15', rainfall_mm: 95.0, cumulative_mm: 515.0 },
  { date: '2023-05-16', rainfall_mm: 120.0, cumulative_mm: 635.0 },
  { date: '2023-05-17', rainfall_mm: 85.0, cumulative_mm: 720.0 },
  { date: '2023-05-18', rainfall_mm: 42.0, cumulative_mm: 762.0 },
  { date: '2023-05-22', rainfall_mm: 4.8, cumulative_mm: 780.0 },
  { date: '2023-06-01', rainfall_mm: 8.0, cumulative_mm: 800.0 },
  { date: '2023-06-15', rainfall_mm: 5.0, cumulative_mm: 820.0 },
  { date: '2023-06-30', rainfall_mm: 3.0, cumulative_mm: 835.0 },
]

// ================================================================
// Scenario definitions
// ================================================================

export const RETROCAST_SCENARIOS: RetrocastScenario[] = [
  {
    id: 'niscemi',
    name: 'Niscemi (CL)',
    subtitle: 'Frana da innesco pluviometrico',
    region: 'Sicilia',
    eventDate: '2026-01-15',
    eventType: 'landslide',
    analysisStart: '2025-10-01',
    analysisEnd: '2026-02-28',
    lat: 37.1498,
    lon: 14.3896,
    maxEW: 9.57,
    maxEWDate: '2026-01-15',
    firstOrangeDate: '2025-12-14',
    firstRedDate: '2026-01-14',
    leadTimeDays: 32,
    totalRainfallMm: 1310,
    maxDailyRainfallMm: 131,
    peakDisplacementMm: -56.62,
    peakVelocityMmYr: -173.88,
    ewTimeline: NISCEMI_EW,
    insarData: NISCEMI_INSAR,
    rainfallData: NISCEMI_RAINFALL,
    keyEvents: [
      { date: '2025-10-04', label: 'Primo YELLOW (EW 5.62)', type: 'info' },
      { date: '2025-11-01', label: 'InSAR: accelerazione rilevata (-29 mm/yr)', type: 'warning' },
      { date: '2025-12-01', label: 'InSAR: velocita -77 mm/yr', type: 'warning' },
      { date: '2025-12-14', label: 'Primo ORANGE (EW 7.03) - Lead time 32 gg', type: 'warning' },
      { date: '2026-01-02', label: 'Pioggia intensa: 50 mm/giorno', type: 'warning' },
      { date: '2026-01-09', label: 'EW 8.24 - Soglia quasi RED', type: 'warning' },
      { date: '2026-01-14', label: 'Primo RED (EW 9.30) - Allerta critica', type: 'critical' },
      { date: '2026-01-15', label: 'EVENTO FRANA - 131 mm pioggia', type: 'critical' },
      { date: '2026-01-18', label: 'Rientro in ORANGE', type: 'info' },
    ],
  },
  {
    id: 'emilia',
    name: 'Emilia-Romagna',
    subtitle: 'Alluvione maggio 2023',
    region: 'Emilia-Romagna',
    eventDate: '2023-05-16',
    eventType: 'flood',
    analysisStart: '2022-11-01',
    analysisEnd: '2023-06-30',
    lat: 44.25,
    lon: 12.30,
    maxEW: 8.43,
    maxEWDate: '2023-05-18',
    firstOrangeDate: '2023-05-15',
    firstRedDate: null,
    leadTimeDays: 14,
    totalRainfallMm: 835,
    maxDailyRainfallMm: 120,
    peakDisplacementMm: -16.99,
    peakVelocityMmYr: -4.0,
    ewTimeline: EMILIA_EW,
    insarData: EMILIA_INSAR,
    rainfallData: EMILIA_RAINFALL,
    keyEvents: [
      { date: '2022-11-01', label: 'Inizio monitoraggio - siccita estrema', type: 'info' },
      { date: '2023-04-25', label: 'SWI al 10° percentile - suolo secco', type: 'info' },
      { date: '2023-05-01', label: 'Prima ondata pluviometrica: 48 mm', type: 'warning' },
      { date: '2023-05-02', label: 'Primo YELLOW (EW 5.17) - 73 mm pioggia', type: 'warning' },
      { date: '2023-05-14', label: 'Seconda ondata - SWI al 90° percentile', type: 'warning' },
      { date: '2023-05-15', label: 'Primo ORANGE (EW 7.68) - 95 mm pioggia', type: 'critical' },
      { date: '2023-05-16', label: 'EVENTO ALLUVIONE - 120 mm pioggia', type: 'critical' },
      { date: '2023-05-18', label: 'Picco EW 8.43 - massimo dello scenario', type: 'critical' },
      { date: '2023-05-22', label: 'Rientro in GREEN', type: 'info' },
    ],
  },
]

// EW level colors for charts
export const EW_LEVEL_COLORS: Record<EWLevel, string> = {
  GREEN: '#10b981',
  YELLOW: '#f59e0b',
  ORANGE: '#f97316',
  RED: '#ef4444',
}

export const EW_LEVEL_LABELS: Record<EWLevel, string> = {
  GREEN: 'Basso',
  YELLOW: 'Moderato',
  ORANGE: 'Elevato',
  RED: 'Critico',
}
